<script>
import Vue from 'vue'
import { MODAL } from '@constants/Text'
import { GlobalBus } from '@services/bus'
import rules from '@constants/Rules'
import { apiClientsCarteiraAdministrada } from '@modules/clients/api/apiClientsCarteiraAdministrada.js'
import {
  ANBIMA_REPORT_FORM_TO_API_MAP,
  ANBIMA_REPORT_API_TO_FORM_MAP,
  ANBIMA_REPORT_OPTIONS,
  ANBIMA_REPORT_LABELS,
} from '@modules/clients/constants/Text.js'
import { mapAnbimaReportFields } from '@modules/clients/utils/reportAnbima.js'
import Title from '@components/section-title'
import AutocompleteMany from '@components/autocomplete-many/autocomplete-many.vue'

export default {
  components: { Title, AutocompleteMany },
  data() {
    return {
      busModal: new Vue(),
      loading: false,
      isEditMode: false,
      item: {},
      form: {
        selectedClient: null,
        codigo: null,
        statusCarteira: null,
        primeiroAporte: null,
        encerramentoContrato: null,
        modeloCarteira: null,
        segmentoInvestidor: null,
        publicoAlvo: null,
        riscoCarteira: null,
        riscoInvestidor: null,
        creditoPrivado: null,
        investimentoExterior: null,
        investimentoCotasFundos: null,
        derivativos: null,
        estrategiasDerivativos: null,
        tipoTaxaGestao: null,
        valorTaxaGestao: null,
        descricaoTaxaGestao: null,
        cobrancaTaxaComplementar: null,
        tipoCobrancaTaxaComplementar: null,
        valorTaxaComplementar: null,
        descricaoTaxaComplementar: null,
        cobrancaTaxaPerformance: null,
        tipoCobrancaTaxaPerformance: null,
        valorTaxaPerformance: null,
        descricaoTaxaPerformance: null,
        utilizaBenchmark: null,
        benchmark: null,
        custodianteContratado: null,
        cnpjCustodianteContratado: null,
        aprecamentoCarteira: null,
        cnpjResponsavelAprecamento: null,
        rentabilidadeTir: null,
        aportes: null,
        resgates: null,
        volumeFinanceiro: null,
        valorAplicadoCotas: null,
        criptoativos: null,
        campoApoio: null,
        controladorContratado: null,
        cnpjControlador: null,
      },
      options: ANBIMA_REPORT_OPTIONS,
      rules: {
        ...rules,
        maxLength: (max) => (v) =>
          !v || v.length <= max || `Deve conter no máximo ${max} caracteres`,
        requiredArray: (v) => {
          const requiredText = this.rules && this.rules.required('')
          return (Array.isArray(v) ? v.length > 0 : !!v) || requiredText
        },
      },
    }
  },
  computed: {
    clients() {
      return this.$store.getters['user/allClients'] || []
    },
    selectedClientName() {
      const client = (this.clients || []).find(
        (client) => client.id === this.form.selectedClient
      )
      return (client && client.name) || 'selecionado'
    },
    codigoMessages() {
      const digits = String(this.form.codigo || '').replace(/\D/g, '')
      if (!digits) return ''
      return `Deve ter 14 caracteres numéricos. Você digitou ${digits.length}/14.`
    },
    fieldDisabled() {
      const YES_OPTION = 'Sim'
      const GENERAL_INVESTORS = 'Investidores em geral'
      const CLOSED_STATUS = 'Encerrada'
      const FEE_TYPES = ['Fixa', 'Percentual']
      const SPECIFIC_TYPE = 'Específica'

      const {
        statusCarteira,
        publicoAlvo,
        derivativos,
        tipoCobrancaTaxaComplementar,
        tipoTaxaGestao,
        cobrancaTaxaComplementar,
        cobrancaTaxaPerformance,
        tipoCobrancaTaxaPerformance,
        utilizaBenchmark,
        custodianteContratado,
        controladorContratado,
      } = this.form

      return {
        encerramentoContrato: statusCarteira !== CLOSED_STATUS,
        publicoAlvo: publicoAlvo !== GENERAL_INVESTORS,
        estrategiasDerivativos: derivativos !== YES_OPTION,
        valorTaxaComplementar: !FEE_TYPES.includes(
          tipoCobrancaTaxaComplementar
        ),
        valorTaxaGestao: !FEE_TYPES.includes(tipoTaxaGestao),
        descricaoTaxaGestao: tipoTaxaGestao !== SPECIFIC_TYPE,
        cobrancaTaxaComplementar: cobrancaTaxaComplementar !== YES_OPTION,
        valorTaxaPerformance: !FEE_TYPES.includes(tipoCobrancaTaxaPerformance),
        benchmark: utilizaBenchmark !== YES_OPTION,
        cnpjCustodianteContratado: custodianteContratado !== YES_OPTION,
        cnpjControlador: controladorContratado !== YES_OPTION,
        cobrancaTaxaPerformance: cobrancaTaxaPerformance !== YES_OPTION,
        descricaoTaxaComplementar:
          tipoCobrancaTaxaComplementar !== SPECIFIC_TYPE,
        descricaoTaxaPerformance: tipoCobrancaTaxaPerformance !== SPECIFIC_TYPE,
      }
    },
    conditionalRules() {
      const R = this.rules
      const MAX_DESCRIPTION_LENGTH = 600

      const table = {
        encerramentoContrato: {
          dependsOn: 'encerramentoContrato',
          rules: [R.required, R.data],
        },
        riscoCarteira: {
          dependsOn: 'publicoAlvo',
          rules: [R.maxLength(MAX_DESCRIPTION_LENGTH)],
        },
        riscoInvestidor: {
          dependsOn: 'publicoAlvo',
          rules: [R.maxLength(MAX_DESCRIPTION_LENGTH)],
        },
        estrategiasDerivativos: {
          dependsOn: 'estrategiasDerivativos',
          rules: [R.requiredArray],
        },
        valorTaxaGestao: { dependsOn: 'valorTaxaGestao', rules: [R.required] },
        descricaoTaxaGestao: {
          dependsOn: 'descricaoTaxaGestao',
          always: [R.maxLength(MAX_DESCRIPTION_LENGTH)],
          rules: [R.required],
        },
        descricaoTaxaComplementar: {
          dependsOn: 'descricaoTaxaComplementar',
          always: [R.maxLength(MAX_DESCRIPTION_LENGTH)],
          rules: [R.required],
        },
        cobrancaTaxaComplementar: {
          dependsOn: 'cobrancaTaxaComplementar',
          rules: [R.required],
        },
        tipoCobrancaTaxaComplementar: {
          dependsOn: 'cobrancaTaxaComplementar',
          rules: [R.required],
        },
        valorTaxaComplementar: {
          dependsOn: 'valorTaxaComplementar',
          rules: [R.required],
        },
        cobrancaTaxaPerformance: {
          dependsOn: 'cobrancaTaxaPerformance',
          rules: [R.required],
        },
        tipoCobrancaTaxaPerformance: {
          dependsOn: 'cobrancaTaxaPerformance',
          rules: [R.required],
        },
        valorTaxaPerformance: {
          dependsOn: 'valorTaxaPerformance',
          rules: [R.required],
        },
        descricaoTaxaPerformance: {
          dependsOn: 'descricaoTaxaPerformance',
          always: [R.maxLength(MAX_DESCRIPTION_LENGTH)],
          rules: [R.required],
        },
        benchmark: { dependsOn: 'benchmark', rules: [R.requiredArray] },
        cnpjCustodianteContratado: {
          dependsOn: 'cnpjCustodianteContratado',
          rules: [R.required, R.cnpj],
        },
        cnpjControlador: {
          dependsOn: 'cnpjControlador',
          rules: [R.required, R.cnpj],
        },
        rentabilidadeTir: { dependsOn: 'publicoAlvo', rules: [R.required] },
      }

      const output = {}

      Object.keys(table).forEach(function (field) {
        const cfg = table[field]
        const isEnabled = !this.fieldDisabled[cfg.dependsOn]
        const alwaysRules = cfg.always || []
        const conditionalRules = isEnabled ? cfg.rules || [] : []
        output[field] = [].concat(alwaysRules).concat(conditionalRules)
      }, this)

      return output
    },
    mappedEstrategiasDerivativos() {
      return (this.options.estrategiasDerivativos || []).map((v) => ({
        id: v,
        text: v,
      }))
    },
    mappedBenchmarks() {
      return (this.options.benchmark || []).map((v) => ({ id: v, text: v }))
    },
    shouldShowCreateNotice() {
      return (
        Boolean(this.form.selectedClient) && !this.loading && !this.isEditMode
      )
    },
    label() {
      const BASE = ANBIMA_REPORT_LABELS

      const out = {}
      Object.keys(BASE).forEach((k) => {
        out[k] = this.isFieldRequired[k] ? `${BASE[k]}*` : BASE[k]
      })
      return out
    },
    isFieldRequired() {
      const hasRequired = (arr = []) =>
        [this.rules.required, this.rules.requiredArray].some((rule) =>
          arr.includes(rule)
        )

      const staticRequired = new Set([
        'selectedClient',
        'statusCarteira',
        'primeiroAporte',
        'modeloCarteira',
        'segmentoInvestidor',
        'publicoAlvo',
        'creditoPrivado',
        'investimentoExterior',
        'investimentoCotasFundos',
        'derivativos',
        'tipoTaxaGestao',
        'cobrancaTaxaComplementar',
        'cobrancaTaxaPerformance',
        'utilizaBenchmark',
        'custodianteContratado',
        'controladorContratado',
        'aprecamentoCarteira',
        'cnpjResponsavelAprecamento',
        'aportes',
        'resgates',
        'volumeFinanceiro',
        'valorAplicadoCotas',
        'criptoativos',
      ])

      const dynamicRequired = {
        encerramentoContrato: hasRequired(
          this.conditionalRules.encerramentoContrato
        ),
        riscoCarteira: hasRequired(this.conditionalRules.riscoCarteira),
        riscoInvestidor: hasRequired(this.conditionalRules.riscoInvestidor),
        estrategiasDerivativos: hasRequired(
          this.conditionalRules.estrategiasDerivativos
        ),
        valorTaxaGestao: hasRequired(this.conditionalRules.valorTaxaGestao),
        descricaoTaxaGestao: hasRequired(
          this.conditionalRules.descricaoTaxaGestao
        ),
        tipoCobrancaTaxaComplementar: hasRequired(
          this.conditionalRules.tipoCobrancaTaxaComplementar
        ),
        valorTaxaComplementar: hasRequired(
          this.conditionalRules.valorTaxaComplementar
        ),
        descricaoTaxaComplementar: hasRequired(
          this.conditionalRules.descricaoTaxaComplementar
        ),
        tipoCobrancaTaxaPerformance: hasRequired(
          this.conditionalRules.tipoCobrancaTaxaPerformance
        ),
        valorTaxaPerformance: hasRequired(
          this.conditionalRules.valorTaxaPerformance
        ),
        descricaoTaxaPerformance: hasRequired(
          this.conditionalRules.descricaoTaxaPerformance
        ),
        benchmark: hasRequired(this.conditionalRules.benchmark),
        cnpjCustodianteContratado: hasRequired(
          this.conditionalRules.cnpjCustodianteContratado
        ),
        cnpjControlador: hasRequired(this.conditionalRules.cnpjControlador),
        rentabilidadeTir: hasRequired(this.conditionalRules.rentabilidadeTir),
      }

      const out = {}
      staticRequired.forEach((k) => {
        out[k] = true
      })
      Object.assign(out, dynamicRequired)
      return out
    },
  },
  watch: {
    'form.selectedClient': {
      handler(newVal) {
        if (newVal) {
          this.loadData()
        }
      },
    },
    'form.statusCarteira'(newVal, oldVal) {
      if (oldVal == null) return
      if (newVal !== 'Encerrada') {
        this.form.encerramentoContrato = null
      }
    },
    'form.publicoAlvo'(newVal, oldVal) {
      if (oldVal == null) return
      if (newVal !== 'Investidores em geral') {
        this.form.riscoCarteira = null
        this.form.riscoInvestidor = null
        this.form.rentabilidadeTir = null
      }
    },
    'form.derivativos'(newVal, oldVal) {
      if (oldVal == null) return
      if (newVal !== 'Sim') {
        this.form.estrategiasDerivativos = null
      }
    },
    'form.tipoTaxaGestao'(newVal, oldVal) {
      if (oldVal == null) return
      const feeTypes = ['Fixa', 'Percentual']
      if (!feeTypes.includes(newVal)) {
        this.form.valorTaxaGestao = null
      }
      if (newVal !== 'Específica') {
        this.form.descricaoTaxaGestao = null
      }
    },
    'form.cobrancaTaxaComplementar'(newVal, oldVal) {
      if (oldVal == null) return
      if (newVal !== 'Sim') {
        this.form.tipoCobrancaTaxaComplementar = null
        this.form.valorTaxaComplementar = null
        this.form.descricaoTaxaComplementar = null
      }
    },
    'form.cobrancaTaxaPerformance'(newVal, oldVal) {
      if (oldVal == null) return
      if (newVal !== 'Sim') {
        this.form.tipoCobrancaTaxaPerformance = null
        this.form.valorTaxaPerformance = null
        this.form.descricaoTaxaPerformance = null
      }
    },
    'form.tipoCobrancaTaxaComplementar'(newVal, oldVal) {
      if (oldVal == null) return
      const feeTypes = ['Fixa', 'Percentual']
      if (!feeTypes.includes(newVal)) {
        this.form.valorTaxaComplementar = null
      }
      if (newVal !== 'Específica') {
        this.form.descricaoTaxaComplementar = null
      }
    },
    'form.utilizaBenchmark'(newVal, oldVal) {
      if (oldVal == null) return
      if (newVal !== 'Sim') {
        this.form.benchmark = null
      }
    },
    'form.custodianteContratado'(newVal, oldVal) {
      if (oldVal == null) return
      if (newVal !== 'Sim') {
        this.form.cnpjCustodianteContratado = null
      }
    },
    'form.controladorContratado'(newVal, oldVal) {
      if (oldVal == null) return
      if (newVal !== 'Sim') {
        this.form.cnpjControlador = null
      }
    },
  },
  created() {
    GlobalBus.$on(MODAL.CLIENTS_CARTEIRA_ADMINISTRADA, this.open)
  },
  beforeDestroy() {
    GlobalBus.$off(MODAL.CLIENTS_CARTEIRA_ADMINISTRADA, this.open)
  },
  methods: {
    clear() {
      this.item = {}
      this.isEditMode = false
      Object.keys(this.form).forEach((key) => {
        if (key !== 'selectedClient') {
          this.form[key] = null
        }
      })
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
    },
    open({ item = {} } = {}) {
      this.clear()
      this.item = { ...(item || {}) }

      const prevClientId = this.form.selectedClient
      const clientId = this.item.id_cliente || this.item.id || null

      this.form.selectedClient = clientId
      this.busModal.$emit('dialog', true)

      if (clientId && prevClientId === clientId) {
        this.loadData()
      }
    },
    async loadData() {
      if (!this.form.selectedClient) return
      this.loading = true
      const r = await apiClientsCarteiraAdministrada.loadData({
        idClient: this.form.selectedClient,
        forceUpdate: true,
      })
      if (r && r.status === 200) {
        const formData = mapAnbimaReportFields(
          r.value,
          ANBIMA_REPORT_API_TO_FORM_MAP
        )
        this.form = {
          ...this.form,
          ...formData,
        }
        this.isEditMode = true
      } else {
        this.clear()
      }
      this.loading = false
    },
    async save() {
      if (this.loading) return
      if (!this.$refs.form.validate()) return

      this.loading = true
      const payload = mapAnbimaReportFields(
        this.form,
        ANBIMA_REPORT_FORM_TO_API_MAP
      )
      const saveMethod = this.isEditMode
        ? apiClientsCarteiraAdministrada.edit
        : apiClientsCarteiraAdministrada.save

      const r = await saveMethod({
        id: this.form.selectedClient,
        data: payload,
      })

      if (r.status === 200) {
        this.busModal.$emit('dialog', false)
      }
      this.$showSnackModal(r)
      this.loading = false
    },
    async remove() {
      if (this.loading || !this.form.selectedClient) return
      this.loading = true
      const r = await apiClientsCarteiraAdministrada.remove({
        id: this.form.selectedClient,
      })
      if (r.status === 200) {
        this.busModal.$emit('dialog', false)
      }
      this.$showSnackModal(r)
      this.loading = false
    },
    confirmRemove() {
      if (!this.form.selectedClient || this.loading) return
      this.$showModal({
        title: 'Excluir carteira',
        text: `Tem certeza que deseja excluir o registro de carteira administrada do cliente "${this.selectedClientName}"?`,
        v2: true,
        type: 'neutral',
        center: true,
        buttons: [
          { text: 'Cancelar', class: { underlined: true } },
          {
            text: 'Excluir',
            class: { primary: true },
            action: () => this.remove(),
          },
        ],
      })
    },
  },
}
</script>

<template>
  <BaseModal maxWidth="1100px" :bus="busModal" persistent>
    <template v-slot:title>
      <Title title="Registro de Carteira Administrada" underlined />
    </template>
    <div class="carteira-administrada">
      <BaseAlert
        v-if="shouldShowCreateNotice"
        class="carteira-administrada__notice"
        icon-color="warning"
        icon="warning"
        status="warning"
      >
        <span class="text-normal">
          Nenhum registro encontrado para o cliente selecionado. Preencha os
          dados abaixo para cadastrar.
        </span>
      </BaseAlert>

      <v-form ref="form">
        <v-row class="mt-4" justify="space-between">
          <v-col cols="auto">
            <label class="carteira-administrada__label">
              {{ label.selectedClient }}
            </label>
            <v-autocomplete
              v-model="form.selectedClient"
              class="new-ux"
              placeholder="Selecionar"
              outlined
              :disabled="loading"
              :items="clients"
              item-value="id"
              item-text="name"
              :rules="[rules.required]"
            />
          </v-col>

          <v-col cols="auto" class="mt-6">
            <BaseButton
              v-if="isEditMode"
              secondary
              text
              small
              :loading="loading"
              :disabled="!form.selectedClient || loading"
              @click.prevent="confirmRemove"
            >
              <icon-svg color="red" name="remove" />
              Excluir carteira
            </BaseButton>
          </v-col>
        </v-row>

        <div v-if="form.selectedClient && !loading">
          <v-row>
            <v-col cols="12">
              <div class="carteira-administrada__subtitle">Dados Gerais</div>
            </v-col>

            <v-col cols="4">
              <label class="carteira-administrada__label">
                Código Anbima
              </label>
              <v-text-field
                v-model="form.codigo"
                type="number"
                class="new-ux"
                outlined
                :hint="codigoMessages"
              />
            </v-col>

            <v-col cols="4">
              <label class="carteira-administrada__label">
                {{ label.statusCarteira }}
              </label>
              <v-autocomplete
                v-model="form.statusCarteira"
                class="new-ux"
                placeholder="Selecionar"
                outlined
                :items="options.statusCarteira"
                :rules="[rules.required]"
              />
            </v-col>

            <v-col cols="2">
              <label class="carteira-administrada__label">
                {{ label.primeiroAporte }}
              </label>
              <input-by-type
                v-model="form.primeiroAporte"
                class="new-ux"
                type="date"
                placeholder="DD/MM/AAAA"
                outlined
                :rules="[rules.required, rules.data]"
              />
            </v-col>

            <v-col cols="2">
              <label
                class="carteira-administrada__label"
                :class="{ 'text-disabled': fieldDisabled.encerramentoContrato }"
              >
                {{ label.encerramentoContrato }}
              </label>
              <input-by-type
                v-model="form.encerramentoContrato"
                class="new-ux"
                type="date"
                placeholder="DD/MM/AAAA"
                outlined
                :disabled="fieldDisabled.encerramentoContrato"
                :rules="conditionalRules.encerramentoContrato"
              />
            </v-col>

            <v-col cols="4">
              <label class="carteira-administrada__label">
                {{ label.modeloCarteira }}
              </label>
              <v-autocomplete
                v-model="form.modeloCarteira"
                class="new-ux"
                placeholder="Selecionar"
                outlined
                :items="options.modeloCarteira"
                :rules="[rules.required]"
              />
            </v-col>

            <v-col cols="4">
              <label class="carteira-administrada__label">
                {{ label.segmentoInvestidor }}
              </label>
              <v-autocomplete
                v-model="form.segmentoInvestidor"
                class="new-ux"
                placeholder="Selecionar"
                outlined
                :items="options.segmentoInvestidor"
                :rules="[rules.required]"
              />
            </v-col>

            <v-col cols="4">
              <label class="carteira-administrada__label">
                {{ label.publicoAlvo }}
              </label>
              <v-autocomplete
                v-model="form.publicoAlvo"
                class="new-ux"
                placeholder="Selecionar"
                outlined
                :items="options.publicoAlvo"
                :rules="[rules.required]"
              />
            </v-col>

            <v-col cols="4">
              <label
                class="carteira-administrada__label"
                :class="{ 'text-disabled': fieldDisabled.publicoAlvo }"
              >
                {{ label.riscoCarteira }}
              </label>
              <input-by-type
                v-model="form.riscoCarteira"
                class="new-ux"
                type="text"
                outlined
                :disabled="fieldDisabled.publicoAlvo"
                :rules="conditionalRules.riscoCarteira"
              />
            </v-col>

            <v-col cols="4">
              <label
                class="carteira-administrada__label"
                :class="{ 'text-disabled': fieldDisabled.publicoAlvo }"
              >
                {{ label.riscoInvestidor }}
              </label>
              <input-by-type
                v-model="form.riscoInvestidor"
                class="new-ux"
                type="text"
                outlined
                :disabled="fieldDisabled.publicoAlvo"
                :rules="conditionalRules.riscoInvestidor"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <div class="carteira-administrada__subtitle">Permissões</div>
            </v-col>

            <v-col cols="4">
              <label class="carteira-administrada__label">
                {{ label.creditoPrivado }}
              </label>
              <v-autocomplete
                v-model="form.creditoPrivado"
                class="new-ux"
                placeholder="Selecionar"
                outlined
                :items="options.simNao"
                :rules="[rules.required]"
              />
            </v-col>

            <v-col cols="4">
              <label class="carteira-administrada__label">
                {{ label.investimentoExterior }}
              </label>
              <v-autocomplete
                v-model="form.investimentoExterior"
                class="new-ux"
                placeholder="Selecionar"
                outlined
                :items="options.simNao"
                :rules="[rules.required]"
              />
            </v-col>

            <v-col cols="4">
              <label class="carteira-administrada__label">
                {{ label.investimentoCotasFundos }}
              </label>
              <v-autocomplete
                v-model="form.investimentoCotasFundos"
                class="new-ux"
                placeholder="Selecionar"
                outlined
                :items="options.simNao"
                :rules="[rules.required]"
              />
            </v-col>

            <v-col cols="4">
              <label class="carteira-administrada__label">
                {{ label.derivativos }}
              </label>
              <v-autocomplete
                v-model="form.derivativos"
                class="new-ux"
                placeholder="Selecionar"
                outlined
                :items="options.simNao"
                :rules="[rules.required]"
              />
            </v-col>

            <v-col cols="4">
              <label
                class="carteira-administrada__label"
                :class="{
                  'text-disabled': fieldDisabled.estrategiasDerivativos,
                }"
              >
                {{ label.estrategiasDerivativos }}
              </label>
              <AutocompleteMany
                class="carteira-administrada__select-multiple"
                :items="mappedEstrategiasDerivativos"
                :selected.sync="form.estrategiasDerivativos"
                :multiple="true"
                :selected-items-to-show="2"
                :loading-props="loading"
                :disabled-props="fieldDisabled.estrategiasDerivativos"
                :rules="conditionalRules.estrategiasDerivativos"
                :show-details="true"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12">
              <div class="carteira-administrada__subtitle">Taxas</div>
            </v-col>

            <v-col cols="4">
              <label class="carteira-administrada__label">
                {{ label.tipoTaxaGestao }}
              </label>
              <v-autocomplete
                v-model="form.tipoTaxaGestao"
                class="new-ux"
                placeholder="Selecionar"
                outlined
                :items="options.tipoTaxaGestao"
                :rules="[rules.required]"
              />
            </v-col>

            <v-col cols="4">
              <label
                class="carteira-administrada__label"
                :class="{ 'text-disabled': fieldDisabled.valorTaxaGestao }"
              >
                {{ label.valorTaxaGestao }}
              </label>
              <input-by-type
                v-model="form.valorTaxaGestao"
                class="new-ux"
                :type="
                  form.tipoTaxaGestao === 'Percentual' ? 'percent' : 'money'
                "
                outlined
                :disabled="fieldDisabled.valorTaxaGestao"
                :rules="conditionalRules.valorTaxaGestao"
              />
            </v-col>

            <v-col cols="12">
              <label class="carteira-administrada__label">
                {{ label.descricaoTaxaGestao }}
              </label>
              <input-by-type
                v-model="form.descricaoTaxaGestao"
                class="new-ux"
                type="text"
                outlined
                :rules="conditionalRules.descricaoTaxaGestao"
              />
            </v-col>

            <v-col cols="4">
              <label class="carteira-administrada__label">
                {{ label.cobrancaTaxaComplementar }}
              </label>
              <v-autocomplete
                v-model="form.cobrancaTaxaComplementar"
                class="new-ux"
                placeholder="Selecionar"
                outlined
                :items="options.simNao"
                :rules="[rules.required]"
              />
            </v-col>

            <v-col cols="4">
              <label
                class="carteira-administrada__label"
                :class="{
                  'text-disabled': fieldDisabled.cobrancaTaxaComplementar,
                }"
              >
                {{ label.tipoCobrancaTaxaComplementar }}
              </label>
              <v-autocomplete
                v-model="form.tipoCobrancaTaxaComplementar"
                class="new-ux"
                placeholder="Selecionar"
                outlined
                :items="options.tipoCobrancaTaxa"
                :disabled="fieldDisabled.cobrancaTaxaComplementar"
                :rules="conditionalRules.tipoCobrancaTaxaComplementar"
              />
            </v-col>

            <v-col cols="4">
              <label
                class="carteira-administrada__label"
                :class="{
                  'text-disabled': fieldDisabled.valorTaxaComplementar,
                }"
              >
                {{ label.valorTaxaComplementar }}
              </label>
              <input-by-type
                v-model="form.valorTaxaComplementar"
                class="new-ux"
                :type="
                  form.tipoCobrancaTaxaComplementar === 'Percentual'
                    ? 'percent'
                    : 'money'
                "
                outlined
                :disabled="fieldDisabled.valorTaxaComplementar"
                :rules="conditionalRules.valorTaxaComplementar"
              />
            </v-col>

            <v-col cols="12">
              <label class="carteira-administrada__label">
                {{ label.descricaoTaxaComplementar }}
              </label>
              <input-by-type
                v-model="form.descricaoTaxaComplementar"
                type="text"
                outlined
                class="new-ux"
                :rules="conditionalRules.descricaoTaxaComplementar"
              />
            </v-col>

            <v-col cols="4">
              <label class="carteira-administrada__label">
                {{ label.cobrancaTaxaPerformance }}
              </label>
              <v-autocomplete
                v-model="form.cobrancaTaxaPerformance"
                class="new-ux"
                placeholder="Selecionar"
                outlined
                :items="options.simNao"
                :rules="[rules.required]"
              />
            </v-col>

            <v-col cols="4">
              <label
                class="carteira-administrada__label"
                :class="{
                  'text-disabled': fieldDisabled.cobrancaTaxaPerformance,
                }"
              >
                {{ label.tipoCobrancaTaxaPerformance }}
              </label>
              <v-autocomplete
                v-model="form.tipoCobrancaTaxaPerformance"
                class="new-ux"
                placeholder="Selecionar"
                outlined
                :items="options.tipoCobrancaTaxa"
                :disabled="fieldDisabled.cobrancaTaxaPerformance"
                :rules="conditionalRules.tipoCobrancaTaxaPerformance"
              />
            </v-col>

            <v-col cols="4">
              <label
                class="carteira-administrada__label"
                :class="{
                  'text-disabled': fieldDisabled.valorTaxaPerformance,
                }"
              >
                {{ label.valorTaxaPerformance }}
              </label>
              <input-by-type
                v-model="form.valorTaxaPerformance"
                class="new-ux"
                :type="
                  form.tipoCobrancaTaxaPerformance === 'Percentual'
                    ? 'percent'
                    : 'money'
                "
                outlined
                :disabled="fieldDisabled.valorTaxaPerformance"
                :rules="conditionalRules.valorTaxaPerformance"
              />
            </v-col>

            <v-col cols="12">
              <label class="carteira-administrada__label">
                {{ label.descricaoTaxaPerformance }}
              </label>
              <input-by-type
                v-model="form.descricaoTaxaPerformance"
                class="new-ux"
                type="text"
                outlined
                :rules="conditionalRules.descricaoTaxaPerformance"
              />
            </v-col>
          </v-row>

          <div style="border-top: 1px solid #8e8e8e80; margin-block: 16px">
          </div>

          <v-row>
            <v-col cols="4">
              <label class="carteira-administrada__label">
                {{ label.utilizaBenchmark }}
              </label>
              <v-autocomplete
                v-model="form.utilizaBenchmark"
                class="new-ux"
                placeholder="Selecionar"
                outlined
                :items="options.simNao"
                :rules="[rules.required]"
              />
            </v-col>

            <v-col cols="4">
              <label
                class="carteira-administrada__label"
                :class="{
                  'text-disabled': fieldDisabled.benchmark,
                }"
              >
                {{ label.benchmark }}
              </label>
              <AutocompleteMany
                class="carteira-administrada__select-multiple"
                :items="mappedBenchmarks"
                :selected.sync="form.benchmark"
                :multiple="true"
                :selected-items-to-show="2"
                :loading-props="loading"
                :disabled-props="fieldDisabled.benchmark"
                :rules="conditionalRules.benchmark"
                :show-details="true"
              />
            </v-col>

            <v-col cols="4"></v-col>

            <v-col cols="4">
              <label class="carteira-administrada__label">
                {{ label.custodianteContratado }}
              </label>
              <v-autocomplete
                v-model="form.custodianteContratado"
                class="new-ux"
                placeholder="Selecionar"
                outlined
                :items="options.simNao"
                :rules="[rules.required]"
              />
            </v-col>

            <v-col cols="4">
              <label
                class="carteira-administrada__label"
                :class="{
                  'text-disabled': fieldDisabled.cnpjCustodianteContratado,
                }"
              >
                {{ label.cnpjCustodianteContratado }}
              </label>
              <input-by-type
                v-model="form.cnpjCustodianteContratado"
                class="new-ux"
                type="cnpj"
                outlined
                :disabled="fieldDisabled.cnpjCustodianteContratado"
                :rules="conditionalRules.cnpjCustodianteContratado"
              />
            </v-col>

            <v-col cols="4"></v-col>

            <v-col cols="4">
              <label class="carteira-administrada__label">
                {{ label.controladorContratado }}
              </label>
              <v-autocomplete
                v-model="form.controladorContratado"
                class="new-ux"
                placeholder="Selecionar"
                outlined
                :items="options.simNao"
                :rules="[rules.required]"
              />
            </v-col>

            <v-col cols="4">
              <label
                class="carteira-administrada__label"
                :class="{
                  'text-disabled': fieldDisabled.cnpjControlador,
                }"
              >
                {{ label.cnpjControlador }}
              </label>
              <input-by-type
                v-model="form.cnpjControlador"
                class="new-ux"
                type="cnpj"
                outlined
                :disabled="fieldDisabled.cnpjControlador"
                :rules="conditionalRules.cnpjControlador"
              />
            </v-col>

            <v-col cols="4"></v-col>

            <v-col cols="4">
              <label class="carteira-administrada__label">
                {{ label.aprecamentoCarteira }}
              </label>
              <v-autocomplete
                v-model="form.aprecamentoCarteira"
                class="new-ux"
                placeholder="Selecionar"
                outlined
                :items="options.aprecamentoCarteira"
                :rules="[rules.required]"
              />
            </v-col>

            <v-col cols="4">
              <label class="carteira-administrada__label">
                {{ label.cnpjResponsavelAprecamento }}
              </label>
              <input-by-type
                v-model="form.cnpjResponsavelAprecamento"
                class="new-ux"
                type="cnpj"
                outlined
                :rules="[rules.required, rules.cnpj]"
              />
            </v-col>

            <v-col cols="4">
              <label
                class="carteira-administrada__label"
                :class="{
                  'text-disabled': fieldDisabled.publicoAlvo,
                }"
              >
                {{ label.rentabilidadeTir }}
              </label>
              <input-by-type
                v-model="form.rentabilidadeTir"
                class="new-ux"
                type="percent"
                placeholder="0,00"
                outlined
                :decimal-length="{ min: 2, max: 7 }"
                :disabled="fieldDisabled.publicoAlvo"
                :rules="conditionalRules.rentabilidadeTir"
              />
            </v-col>

            <v-col cols="4">
              <label class="carteira-administrada__label">
                {{ label.aportes }}
              </label>
              <input-by-type
                v-model="form.aportes"
                class="new-ux"
                type="money"
                placeholder="0,00"
                outlined
                :rules="[rules.required]"
              />
            </v-col>

            <v-col cols="4">
              <label class="carteira-administrada__label">
                {{ label.resgates }}
              </label>
              <input-by-type
                v-model="form.resgates"
                class="new-ux"
                type="money"
                placeholder="0,00"
                outlined
                :rules="[rules.required]"
              />
            </v-col>

            <v-col cols="4">
              <label class="carteira-administrada__label">
                {{ label.volumeFinanceiro }}
              </label>
              <input-by-type
                v-model="form.volumeFinanceiro"
                class="new-ux"
                type="money"
                placeholder="0,00"
                outlined
                :rules="[rules.required]"
              />
            </v-col>

            <v-col cols="4">
              <label class="carteira-administrada__label">
                {{ label.valorAplicadoCotas }}
              </label>
              <input-by-type
                v-model="form.valorAplicadoCotas"
                class="new-ux"
                type="money"
                placeholder="0,00"
                outlined
                :rules="[rules.required]"
              />
            </v-col>

            <v-col cols="4">
              <label class="carteira-administrada__label">
                {{ label.criptoativos }}
              </label>
              <v-autocomplete
                v-model="form.criptoativos"
                class="new-ux"
                placeholder="Selecionar"
                outlined
                :items="options.criptoativos"
                :rules="[rules.required]"
              />
            </v-col>

            <v-col cols="4">
              <label class="carteira-administrada__label">
                Campo de apoio
              </label>
              <input-by-type
                v-model="form.campoApoio"
                class="new-ux"
                type="text"
                outlined
              />
            </v-col>
          </v-row>

          <div
            class="carteira-administrada__required-hint text-xs text-color-900"
          >
            <span>* Campos obrigatórios</span>
          </div>
        </div>
      </v-form>
    </div>

    <div style="border-top: 1px solid #8e8e8e80; margin-block: 16px"> </div>

    <template v-slot:actions="{ close }">
      <BaseButton underlined @click="close"> Cancelar </BaseButton>
      <BaseButton primary :loading="loading" @click="save"> Salvar </BaseButton>
    </template>
  </BaseModal>
</template>

<style lang="sass">
.carteira-administrada
  &__label
    font-size: 14px
    display: inline-block
    margin-bottom: 6px

  &__subtitle
    font-size: 14px
    color: var(--black)
    font-weight: bold
    text-transform: uppercase
    padding-bottom: 12px
    margin-bottom: 24px
    border-bottom: 1px solid #8E8E8E80

  &__notice
    border-radius: 10px

  &__select-multiple .v-autocomplete
    margin-top: 0px !important

  .col-2, .col-4, .col-12
    padding-block: 0px

  .v-input.v-input--is-disabled.v-text-field:not(.v-select):not(.v-autocomplete)
    opacity: 0.4

  .v-input__slot
    .v-input__prepend-inner
      margin: auto
</style>
